/**
 * Product Intelligence — the pure half of the product page's Intelligence tab.
 *
 * Ported from the Supernova mobile app (lib/features/product_intelligence) so
 * the dashboard and the app tell an owner the same thing about the same
 * product: the same price windows, the same vendor ranking and the same
 * insight rules. No I/O and no clock reads — `now` is always passed in — so
 * everything here is testable without a backend.
 */
import { formatCurrency } from './utils';

// ─── Price points ──────────────────────────────────────────────────────────

/**
 * One vendor price point. The same shape carries both series:
 * - quoted: a `vendor_price_history` row (products service) — what a vendor
 *   says it charges;
 * - paid: a `purchase_order_items.cost_per_unit` row (dashboard API) — what
 *   the business actually paid on an order. `oldPrice` there is the previous
 *   cost paid to that vendor, and `changedAt` is the order date.
 */
export interface PricePoint {
  payeeId: string;
  payeeName: string;
  /** Price after the change. The chart is plotted on this. */
  newPrice: number;
  /** Price before the change; null on an opening price. */
  oldPrice: number | null;
  reason: string | null;
  changedAt: Date;
  /** Paid series only. */
  orderNumber: string | null;
}

export type PriceSeriesKind = 'quoted' | 'paid';

function toNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function text(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}

/**
 * Parses one API row. Returns null when the row can't anchor a point on the
 * chart — no price, no date or no vendor — rather than inventing a zero.
 */
export function parsePricePoint(row: any): PricePoint | null {
  if (!row || typeof row !== 'object') return null;
  const price = toNumber(row.newPrice ?? row.new_price);
  const rawDate = row.changedAt ?? row.changed_at;
  const at = rawDate ? new Date(rawDate) : null;
  const payeeId = text(row.payeeId ?? row.payee_id);
  if (price === null || !at || Number.isNaN(at.getTime()) || !payeeId) return null;
  return {
    payeeId,
    payeeName: text(row.payeeName ?? row.payee_name) ?? 'Unknown vendor',
    newPrice: price,
    oldPrice: toNumber(row.oldPrice ?? row.old_price),
    reason: text(row.reason),
    changedAt: at,
    orderNumber: text(row.orderNumber),
  };
}

/** Parses a feed, drops unusable rows, and sorts oldest first. */
export function parsePricePoints(rows: unknown): PricePoint[] {
  if (!Array.isArray(rows)) return [];
  return rows
    .map(parsePricePoint)
    .filter((p): p is PricePoint => p !== null)
    .sort((a, b) => a.changedAt.getTime() - b.changedAt.getTime());
}

/** Signed fractional change of one point (0.026 = 2.6% up), or null. */
export function pointDeltaFraction(p: PricePoint): number | null {
  if (p.oldPrice === null || p.oldPrice === 0) return null;
  return (p.newPrice - p.oldPrice) / p.oldPrice;
}

// ─── Price windows ─────────────────────────────────────────────────────────

export interface PriceWindow {
  key: 'd30' | 'm3' | 'm6' | 'y1';
  days: number;
  /** Selector label. */
  label: string;
  /** Used in prose — "no price changes in the last 3 months". */
  fullName: string;
}

/**
 * Only the year is fetched. Price changes are rare, so the tab loads one year
 * once and derives the shorter windows in memory — switching never spins.
 */
export const PRICE_WINDOWS: readonly PriceWindow[] = [
  { key: 'd30', days: 30, label: '30D', fullName: 'last 30 days' },
  { key: 'm3', days: 90, label: '3M', fullName: 'last 3 months' },
  { key: 'm6', days: 180, label: '6M', fullName: 'last 6 months' },
  { key: 'y1', days: 365, label: '1Y', fullName: 'last year' },
];

export const FETCH_WINDOW = PRICE_WINDOWS[3];

/** Three months: wide enough that most products have a change in it, narrow
 *  enough that the trend still reflects what a vendor is doing now. */
export const INITIAL_WINDOW = PRICE_WINDOWS[1];

const DAY_MS = 24 * 60 * 60 * 1000;

export function windowStart(window: PriceWindow, now: Date): Date {
  return new Date(now.getTime() - window.days * DAY_MS);
}

// ─── Vendor series ─────────────────────────────────────────────────────────

/** One vendor's price line within a window. */
export interface VendorSeries {
  payeeId: string;
  payeeName: string;
  /** Oldest → newest, never empty. */
  points: PricePoint[];
  /** Price at the end of the window. */
  currentPrice: number;
  /** Price at the start of the window: the first change's "before" when it
   *  has one, else its "after". */
  openingPrice: number;
  /** Signed fractional movement across the window, or null when there is
   *  nothing to compare (zero opening price, or a lone opening point). */
  windowDeltaFraction: number | null;
}

export interface PriceSeries {
  window: PriceWindow;
  /** Cheapest current price first, so the legend reads as a ranking. */
  vendors: VendorSeries[];
  /** Every point in the window, newest first — backs the change log. */
  allPointsDescending: PricePoint[];
}

export function cheapestVendor(series: PriceSeries): VendorSeries | null {
  return series.vendors[0] ?? null;
}

/** The dearest vendor, or null when fewer than two vendors are in play — a
 *  spread needs two prices. */
export function dearestVendor(series: PriceSeries): VendorSeries | null {
  return series.vendors.length < 2 ? null : series.vendors[series.vendors.length - 1];
}

function vendorSeries(payeeId: string, points: PricePoint[]): VendorSeries {
  const first = points[0];
  const last = points[points.length - 1];
  const openingPrice = first.oldPrice ?? first.newPrice;
  const currentPrice = last.newPrice;
  let windowDeltaFraction: number | null = null;
  if (openingPrice !== 0 && !(points.length === 1 && first.oldPrice === null)) {
    windowDeltaFraction = (currentPrice - openingPrice) / openingPrice;
  }
  return { payeeId, payeeName: last.payeeName, points, currentPrice, openingPrice, windowDeltaFraction };
}

/** Derives the view for `window` from a year of points. */
export function buildPriceSeries(yearOfPoints: PricePoint[], window: PriceWindow, now: Date): PriceSeries {
  const cutoff = windowStart(window, now).getTime();
  const inWindow = yearOfPoints.filter((p) => p.changedAt.getTime() >= cutoff);

  const byVendor = new Map<string, PricePoint[]>();
  for (const p of inWindow) {
    const list = byVendor.get(p.payeeId) ?? [];
    list.push(p);
    byVendor.set(p.payeeId, list);
  }

  const vendors = [...byVendor.entries()].map(([id, pts]) =>
    vendorSeries(
      id,
      [...pts].sort((a, b) => a.changedAt.getTime() - b.changedAt.getTime()),
    ),
  );
  vendors.sort((a, b) => a.currentPrice - b.currentPrice);

  const allPointsDescending = [...inWindow].sort((a, b) => b.changedAt.getTime() - a.changedAt.getTime());
  return { window, vendors, allPointsDescending };
}

/**
 * Price bounds of the window, padded so a line never sits flush against the
 * chart's edge. A window where every price is identical gets an arbitrary
 * spread so the flat line lands mid-chart. Null when empty.
 */
export function priceBounds(series: PriceSeries): { min: number; max: number } | null {
  let lo = Infinity;
  let hi = -Infinity;
  for (const v of series.vendors) {
    for (const price of [v.openingPrice, ...v.points.map((p) => p.newPrice)]) {
      if (price < lo) lo = price;
      if (price > hi) hi = price;
    }
  }
  if (lo === Infinity) return null;
  if (Math.abs(hi - lo) < 0.0001) {
    const pad = Math.abs(lo) < 0.01 ? 1 : Math.abs(lo) * 0.1;
    return { min: Math.max(0, lo - pad), max: hi + pad };
  }
  const pad = (hi - lo) * 0.15;
  return { min: Math.max(0, lo - pad), max: hi + pad };
}

/**
 * A vendor's line as step points across the whole window: the opening price
 * held from the window's left edge, a step at each change, and the current
 * price carried to today. A step, not a slope — a vendor's price holds and
 * then jumps, and a sloped line would invent prices nobody ever quoted.
 */
export function stepPoints(v: VendorSeries, window: PriceWindow, now: Date): { t: number; price: number }[] {
  const out = [{ t: windowStart(window, now).getTime(), price: v.openingPrice }];
  for (const p of v.points) out.push({ t: p.changedAt.getTime(), price: p.newPrice });
  out.push({ t: now.getTime(), price: v.currentPrice });
  return out;
}

// ─── Stock movements ───────────────────────────────────────────────────────

export type MovementType = 'stock_in' | 'stock_out' | 'stock_count' | 'adjustment' | string;

export interface MovementEntry {
  productId: string | null;
  type: MovementType;
  qtyBefore: number;
  qtyAfter: number;
  /** Already resolved server-side ("Others" replaced by its custom text). */
  reason: string | null;
  userName: string | null;
  createdAt: Date | null;
}

export function parseMovement(row: any): MovementEntry {
  const at = row?.createdAt ? new Date(row.createdAt) : null;
  return {
    productId: text(row?.productId),
    type: text(row?.type) ?? '',
    qtyBefore: toNumber(row?.qtyBefore) ?? 0,
    qtyAfter: toNumber(row?.qtyAfter) ?? 0,
    reason: text(row?.reason),
    userName: text(row?.userName),
    createdAt: at && !Number.isNaN(at.getTime()) ? at : null,
  };
}

/**
 * Parses a movement page and keeps only this product's rows. The products
 * service honours `productId`, so this normally drops nothing; it is kept so a
 * server that ignored the filter could never attribute another product's
 * stock moves to this one.
 */
export function parseMovements(rows: unknown, productId: string): MovementEntry[] {
  if (!Array.isArray(rows)) return [];
  return rows.map(parseMovement).filter((m) => m.productId === productId);
}

// ─── Stock position ────────────────────────────────────────────────────────

/** Where the product stands at one location, or across all of them. */
export interface StockPosition {
  /** 'location' = the header's selected location; 'all' = no location picked. */
  scope: 'location' | 'all';
  /** False when the product isn't set up at the selected location. */
  stocked: boolean;
  qty: number | null;
  threshold: number | null;
  /** Null when unknown — or, for 'all', when it differs between locations. */
  cost: number | null;
  sell: number | null;
  /** For 'all': the price differs between locations, so no single figure. */
  costVaries: boolean;
  sellVaries: boolean;
  /** Units × that location's cost, summed. Null when no cost is known. */
  stockValue: number | null;
}

/** The one value every location shares, or null + varies when they differ. */
function uniform(values: (number | null)[]): { value: number | null; varies: boolean } {
  const known = values.filter((v): v is number => v !== null);
  if (known.length === 0) return { value: null, varies: false };
  const first = known[0];
  const varies = known.some((v) => Math.abs(v - first) > 0.0001);
  return { value: varies ? null : first, varies };
}

/**
 * Reads the stock position off the product record the page already loaded
 * (`GET /products/:id` — `locations` and `prices`, both already limited by the
 * server to the locations this user may see).
 */
export function stockPosition(product: any, locationId: string | null): StockPosition {
  const locations: any[] = Array.isArray(product?.locations) ? product.locations : [];
  const prices: any[] = Array.isArray(product?.prices) ? product.prices : [];

  if (locationId) {
    const loc = locations.find((l) => l.locationId === locationId);
    const price = prices.find((p) => p.locationId === locationId);
    const qty = loc ? toNumber(loc.inStockQty) : null;
    const cost = toNumber(price?.cost);
    return {
      scope: 'location',
      stocked: !!loc,
      qty,
      threshold: loc ? toNumber(loc.reorderThreshold) : null,
      cost,
      sell: toNumber(price?.sellingPrice),
      costVaries: false,
      sellVaries: false,
      stockValue: qty !== null && cost !== null ? qty * cost : null,
    };
  }

  let qty: number | null = null;
  let stockValue: number | null = null;
  const costByLocation = new Map(prices.map((p) => [p.locationId, toNumber(p.cost)]));
  for (const l of locations) {
    const q = toNumber(l.inStockQty) ?? 0;
    qty = (qty ?? 0) + q;
    const c = costByLocation.get(l.locationId) ?? null;
    if (c !== null) stockValue = (stockValue ?? 0) + q * c;
  }
  const cost = uniform(prices.map((p) => toNumber(p.cost)));
  const sell = uniform(prices.map((p) => toNumber(p.sellingPrice)));
  return {
    scope: 'all',
    stocked: locations.length > 0,
    qty,
    threshold: null,
    cost: cost.value,
    sell: sell.value,
    costVaries: cost.varies,
    sellVaries: sell.varies,
    stockValue,
  };
}

/** Gross margin as a fraction (0.25 = 25%), or null when it can't be known. */
export function marginFraction(cost: number | null, sell: number | null): number | null {
  if (cost === null || sell === null || cost <= 0 || sell <= 0) return null;
  return (sell - cost) / sell;
}

export function isBelowReorder(qty: number | null, threshold: number | null): boolean {
  return qty !== null && threshold !== null && threshold > 0 && qty <= threshold;
}

// ─── Insights ──────────────────────────────────────────────────────────────

/** Drives colour and ordering — the capped list keeps what matters most. */
export type InsightLevel = 'critical' | 'warning' | 'info' | 'good';

const LEVEL_ORDER: Record<InsightLevel, number> = { critical: 0, warning: 1, info: 2, good: 3 };

/**
 * One generated sentence about a product. A deterministic rule, not a
 * generated summary: an owner can predict when it appears and trust it when
 * it does.
 */
export interface ProductInsight {
  level: InsightLevel;
  message: string;
  /** Stable rule id — used to de-duplicate and in tests, never shown. */
  rule: string;
}

export function formatQty(q: number): string {
  return Number.isInteger(q) ? String(q) : q.toFixed(2);
}

const pct = (f: number, digits = 0) => `${(f * 100).toFixed(digits)}%`;

/** Rules that only need the stock position: stock against reorder level, and
 *  margin health. */
export function productInsights(pos: StockPosition): ProductInsight[] {
  const out: ProductInsight[] = [];
  const { qty, threshold, cost, sell } = pos;
  const where = pos.scope === 'location' ? 'at this location' : 'at any location';

  if (pos.stocked && qty !== null) {
    if (qty <= 0) {
      out.push({ level: 'critical', message: `Out of stock. Nothing on hand ${where}.`, rule: 'out_of_stock' });
    } else if (isBelowReorder(qty, threshold)) {
      out.push({
        level: 'warning',
        message: `Stock is below the reorder level — ${formatQty(qty)} on hand, reorder at ${formatQty(threshold!)}.`,
        rule: 'below_reorder',
      });
    }
  }

  if (cost !== null && cost > 0 && sell !== null && sell > 0) {
    if (sell <= cost) {
      out.push({
        level: 'critical',
        message: `Selling at or below cost — ${formatCurrency(sell)} against a ${formatCurrency(cost)} unit cost.`,
        rule: 'negative_margin',
      });
    } else {
      const margin = (sell - cost) / sell;
      if (margin < 0.1) {
        out.push({
          level: 'warning',
          message: `Thin margin — ${pct(margin, 1)} at the current price and cost.`,
          rule: 'thin_margin',
        });
      }
    }
  }
  return out;
}

/** Rules over a price window: who is cheapest by how much, and which vendors
 *  are drifting up or down. */
export function priceInsights(series: PriceSeries): ProductInsight[] {
  const out: ProductInsight[] = [];
  const cheapest = cheapestVendor(series);
  const dearest = dearestVendor(series);

  if (cheapest && dearest && dearest.currentPrice > 0) {
    const gap = (dearest.currentPrice - cheapest.currentPrice) / dearest.currentPrice;
    if (gap >= 0.05) {
      out.push({
        level: 'info',
        message:
          `${cheapest.payeeName} is ${pct(gap)} cheaper than ${dearest.payeeName} — ` +
          `${formatCurrency(cheapest.currentPrice)} against ${formatCurrency(dearest.currentPrice)}.`,
        rule: 'vendor_spread',
      });
    }
  }

  for (const v of series.vendors) {
    const d = v.windowDeltaFraction;
    if (d === null) continue;
    if (d >= 0.05) {
      out.push({
        level: 'warning',
        message: `${v.payeeName} has raised its price ${pct(d)} over the ${series.window.fullName}.`,
        rule: 'vendor_price_rise',
      });
    } else if (d <= -0.05) {
      out.push({
        level: 'good',
        message: `${v.payeeName} has dropped its price ${pct(Math.abs(d))} over the ${series.window.fullName}.`,
        rule: 'vendor_price_drop',
      });
    }
  }
  return out;
}

/**
 * Days of cover from the movement ledger. Stock-outs are a proxy for how fast
 * a product leaves the shelf, so the wording says "stock-out rate" rather than
 * claiming a sales figure.
 */
export function movementInsights(movements: MovementEntry[], qty: number | null, now: Date): ProductInsight[] {
  if (qty === null || qty <= 0) return [];
  const windowDays = 30;
  const cutoff = now.getTime() - windowDays * DAY_MS;

  let unitsOut = 0;
  for (const m of movements) {
    if (!m.createdAt || m.createdAt.getTime() < cutoff) continue;
    if (m.type !== 'stock_out') continue;
    const delta = m.qtyBefore - m.qtyAfter;
    if (delta > 0) unitsOut += delta;
  }
  if (unitsOut <= 0) return [];

  const daysLeft = Math.floor(qty / (unitsOut / windowDays));
  return [
    {
      level: daysLeft <= 14 ? 'warning' : 'info',
      message: `About ${daysLeft} days of cover left at the current stock-out rate (${formatQty(unitsOut)} units in ${windowDays} days).`,
      rule: 'days_of_cover',
    },
  ];
}

/** De-duplicates by rule, orders by severity and caps the list — three
 *  competing sentences means none get read. */
export function rankInsights(insights: ProductInsight[], max = 3): ProductInsight[] {
  const seen = new Set<string>();
  const unique: ProductInsight[] = [];
  for (const i of insights) {
    if (seen.has(i.rule)) continue;
    seen.add(i.rule);
    unique.push(i);
  }
  // Array.prototype.sort is stable, so rules of equal severity keep their order.
  unique.sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level]);
  return unique.slice(0, max);
}

// ─── Access ────────────────────────────────────────────────────────────────

/** Who sees the Intelligence tab: owners and admins only. */
export const INTELLIGENCE_ROLES: readonly string[] = ['owner', 'admin'];

export function canSeeProductIntelligence(role: string | null | undefined): boolean {
  return INTELLIGENCE_ROLES.includes(role ?? '');
}
